import { CheckBox } from '@droplet-tech-code/core-elements/module/ui/Checkbox';
import { DottedLine } from '@droplet-tech-code/core-elements/module/ui/Divider/Divider';
import { HStack, VStack } from '@droplet-tech-code/core-elements/module/ui/Stack';
import { Text } from '@droplet-tech-code/core-elements/module/ui/Text';
import { View } from '@droplet-tech-code/core-elements/module/ui/View';
import {
  isNativeMobile,
  isWeb,
} from '@droplet-tech-code/core-elements/module/utils/utils.helper';
import { PurchaseClientWithMetaWithTickets } from '@naus-code/naus-admin-types';
import { useIsSystemUser } from '~/hooks/auth.hook';

import { PurchaseListItemInternalComponent } from '~/screens/Support/Purchases/Purchase.ListItem';
import { TicketDetails } from '~/screens/Support/Purchases/PurchaseItem';
import { translate } from '~/utils/translation.utils';

// Lighten a hex color by mixing it with white. Used to tint the purchase
// background a softer shade of the group's pill color.
const lighten = (hex: string, amount: number): string => {
  const m = hex.replace('#', '');
  const r = parseInt(m.slice(0, 2), 16);
  const g = parseInt(m.slice(2, 4), 16);
  const b = parseInt(m.slice(4, 6), 16);
  const mix = (c: number) => Math.round(c + (255 - c) * amount);
  const toHex = (c: number) => c.toString(16).padStart(2, '0');
  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
};

export const PurchaseTicketsCheckList = ({
  purchase,
  selectedTicketIds,
  onChange,
  groupTintBg,
  disabled,
}: {
  purchase: PurchaseClientWithMetaWithTickets;
  selectedTicketIds: string[];
  onChange: (next: string[]) => void;
  groupTintBg?: string;
  disabled?: boolean;
}) => {
  const tickets = purchase.tickets;
  const allTicketIds = tickets.map((t) => t._id);
  const allChecked =
    allTicketIds.length > 0 && selectedTicketIds.length === allTicketIds.length;

  const isSystem = useIsSystemUser();
  disabled = disabled && !isSystem;

  return (
    <View
      br={8}
      p="2.5"
      style={groupTintBg ? { backgroundColor: groupTintBg } : undefined}
    >
      <VStack space="3" flex={1}>
        <HStack space="3" align="center">
          <CheckBox
            checked={allChecked}
            disabled={disabled}
            onPress={(isChecked) => {
              if (disabled) return;
              onChange(isChecked ? allTicketIds : []);
            }}
          />
          <View flex={1}>
            <PurchaseListItemInternalComponent purchase={purchase} compareMode />
          </View>
        </HStack>
        <View>
          <DottedLine />
        </View>
        <VStack space="4" flex={isWeb ? 1 : undefined}>
          {tickets.map((ticket, iy) => (
            <HStack space="3" key={ticket._id} pl="6">
              <CheckBox
                checked={selectedTicketIds.some((t) => t === ticket._id)}
                disabled={disabled}
                onPress={(isChecked) => {
                  if (disabled) return;
                  const without = selectedTicketIds.filter((t) => t !== ticket._id);
                  onChange(isChecked ? [...without, ticket._id] : without);
                }}
              />
              <VStack space="2" flex={isNativeMobile ? 1 : undefined}>
                <TicketDetails ticket={ticket} ccySymbol={purchase.ccySymbol} />
                <View>{tickets.length - 1 === iy ? null : <DottedLine />}</View>
              </VStack>
            </HStack>
          ))}
        </VStack>
      </VStack>
    </View>
  );
};

export const BookingTicketsCheckList = ({
  purchases,
  selectedByPurchase,
  onChangePurchase,
  onChangeAll,
  groupTagsByPurchaseId,
  disabled,
}: {
  purchases: PurchaseClientWithMetaWithTickets[];
  selectedByPurchase: { [purchaseId: string]: string[] };
  onChangePurchase: (purchaseId: string, next: string[]) => void;
  // Single batch update — used by "select all" so multi-purchase updates
  // don't suffer from stale-closure overwrites.
  onChangeAll: (next: { [purchaseId: string]: string[] }) => void;
  groupTagsByPurchaseId?: {
    [purchaseId: string]: { text: string; bg: string; fg: string };
  };
  disabled?: boolean;
}) => {
  const totalTicketIds = purchases.flatMap((p) => p.tickets.map((t) => t._id));
  const totalSelected = purchases.reduce(
    (sum, p) => sum + (selectedByPurchase[p._id]?.length ?? 0),
    0,
  );
  const allSelected =
    totalTicketIds.length > 0 && totalSelected === totalTicketIds.length;

  const isSystem = useIsSystemUser();
  disabled = disabled && !isSystem;

  return (
    <VStack space="4" m="3" p="3">
      <HStack space="3" align="center">
        <CheckBox
          checked={allSelected}
          disabled={disabled}
          onPress={(isChecked) => {
            if (disabled) return;
            const next: { [purchaseId: string]: string[] } = {};
            for (const p of purchases) {
              next[p._id] = isChecked ? p.tickets.map((t) => t._id) : [];
            }
            onChangeAll(next);
          }}
        />
        <Text.Small color="monochrome-mid">
          {translate('purchases.purchaseTool.selectAllTickets')}
        </Text.Small>
      </HStack>

      <View>
        <DottedLine />
      </View>

      {purchases.map((purchase) => {
        const tag = groupTagsByPurchaseId?.[purchase._id];
        // Strongly lighten the group color for the row background so it reads
        // as a soft tint rather than a saturated block.
        const tintBg = tag ? lighten(tag.bg, 0.55) : undefined;
        return (
          <PurchaseTicketsCheckList
            key={purchase._id}
            purchase={purchase}
            selectedTicketIds={selectedByPurchase[purchase._id] ?? []}
            onChange={(next) => onChangePurchase(purchase._id, next)}
            groupTintBg={tintBg}
            disabled={disabled}
          />
        );
      })}
    </VStack>
  );
};
